'use client'

import { useContext, useEffect, useState } from 'react'
import { ArticleResponseVo } from '@schemas/non-auth'
import color from '@ui/style/color.theme'
import UIContainer from '@components/ui/UIContainer'
import UIFlex from '@components/ui/UIFlex'
import { pxToRem } from '@ui/style/muiTheme'
import { DATE_FORMAT, dayjs } from '@utils/date'
import { ArticleRefContext } from '@page/article/_components/common/ArticleRefContext'
import BgWrapper, { BgWrapperProps } from '@page/article/_components/common/BgWrapper'
import AuthorList, { AuthorListProps } from './Authors/AuthorList'
import TitleBlock from './TitleBlock'
import ArticleUtility from './ArticleUtility'
import useIsScrollDown from './StickyHeader/hooks/useIsScrollDown'
import ScrollProgressBar from './StickyHeader/ScrollProgressBar'
import useIsTargetOffScreen from './StickyHeader/hooks/useTargetOffScreen'
import TitleHeader from './StickyHeader/TitleHeader'
import { useIsCommentDrawControl } from '@store/isCommentDraw'
import isFailedAPI from '@utils/isFailedAPI'
import { isScrollDownState, isTitleOffScreenState } from './StickyHeader/store/articleHeaderState'
import { useRecoilState } from 'recoil'

interface ArticleHeaderProps extends AuthorListProps {
	article: ArticleResponseVo
	bgImg?: BgWrapperProps['src']
	commentVisible: boolean
}
const ArticleHeader = ({
	article,
	journalist,
	isDark,
	bgImg,
	commentVisible,
}: ArticleHeaderProps) => {
	const { setIsCommentDraw } = useIsCommentDrawControl()
	const [scrap, setScrap] = useState<boolean>()
	const [contentString, setContentString] = useState<string>()
	const articleRef = useContext(ArticleRefContext)
	const [isTitleOffScreen, setIsTitleOffScreen] = useRecoilState(isTitleOffScreenState)
	const [isScrollDown, setIsScrollDown] = useRecoilState(isScrollDownState)
	const scrollDown = useIsScrollDown()
	const titleOffScreen = useIsTargetOffScreen(articleRef.titleRef)
	useEffect(() => {
		setIsScrollDown(scrollDown)
		setIsTitleOffScreen(titleOffScreen)
	}, [scrollDown, titleOffScreen, setIsScrollDown, setIsTitleOffScreen])

	useEffect(() => {
		return () => {
			setIsCommentDraw(false)
			setIsScrollDown(false)
			setIsTitleOffScreen(false)
		}
	}, [setIsCommentDraw, setIsScrollDown, setIsTitleOffScreen])
	useEffect(() => {
		if (window) {
			const contentElement = window.document.createElement('div')
			contentElement.innerHTML = article.articleContent ?? ''
			setContentString(contentElement.innerText)
		}
	}, [article])
	return (
		<>
			<UIFlex
				position="relative"
				pt={
					bgImg
						? { xs: pxToRem(141), md: pxToRem(182), lg: pxToRem(192), xl: pxToRem(264) }
						: 0
				}
			>
				{bgImg && <BgWrapper src={bgImg} />}
				<UIContainer>
					<UIFlex
						position="relative"
						pt={
							bgImg
								? 0
								: {
										xs: pxToRem(20),
										md: pxToRem(24),
										lg: pxToRem(28),
										xl: pxToRem(32),
								  }
						}
						pb={
							bgImg
								? {
										xs: pxToRem(20),
										md: pxToRem(24),
										lg: pxToRem(28),
										xl: pxToRem(32),
								  }
								: pxToRem(16)
						}
						borderBottom={bgImg ? 0 : `1px solid ${color.colGray3}`}
						sx={{
							'&>*:not(:first-child)': {
								marginTop: {
									xs: pxToRem(20),
									md: pxToRem(24),
								},
							},
						}}
					>
						<TitleBlock
							title={article.articleTitle}
							mobileTitle={article.articleMobileTitle}
							textContent={contentString}
							sectionName={article.sectionName}
							sectionHref={`/sections/${article?.sectionIdx}`}
							packageName={article?.special?.packageName}
							packageHref={undefined}
							createdAt={dayjs(article.articlePublicationDate).format(
								DATE_FORMAT.CLIENT_MIN,
							)}
							updatedAt={
								article?.articleUpdateDate
									? dayjs(article.articleUpdateDate).format(
											DATE_FORMAT.CLIENT_MIN,
									  )
									: undefined
							}
							isDark={isDark}
						/>
						<UIFlex
							flexDirection={{ xs: 'column', md: 'row' }}
							justifyContent={{ md: 'space-between' }}
							alignItems={{ md: 'center' }}
							sx={{
								'&>*:not(:first-child)': {
									marginLeft: {
										md: pxToRem(16),
									},
									marginTop: {
										xs: pxToRem(16),
										md: 0,
									},
								},
							}}
						>
							{journalist && !isFailedAPI(journalist) && (
								<AuthorList journalist={journalist} isDark={isDark} />
							)}
							<ArticleUtility
								article={article}
								isDark={isDark}
								scrap={scrap}
								commentVisible={commentVisible}
								setScrap={setScrap}
							/>
						</UIFlex>
					</UIFlex>
				</UIContainer>
			</UIFlex>
			<ScrollProgressBar
				articleIdx={article.articleIdx}
				targetAreaRef={articleRef.bodyRef}
				className={isTitleOffScreen ? 'is-pinned' : ''}
				isScrollDown={isScrollDown}
			/>
			<TitleHeader
				article={article}
				className={isTitleOffScreen && isScrollDown ? 'is-pinned' : ''}
				scrap={scrap}
				commentVisible={commentVisible}
				setScrap={setScrap}
			/>
		</>
	)
}

export default ArticleHeader
